/**
 * UndoToast - Toast notifications with undo support
 * Used for destructive actions that can be reversed
 */
import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X, Undo2, Check, AlertTriangle, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import { useUndoToastStore, type UndoToastItem } from '../../hooks/useUndoToast'
import './UndoToast.css'

interface UndoToastProps {
  toast: UndoToastItem
  onUndo: () => void
  onDismiss: () => void
  onRetry?: () => void
}

function SingleUndoToast({ toast, onUndo, onDismiss, onRetry }: UndoToastProps) {
  const [progress, setProgress] = useState(100)
  const [isExiting, setIsExiting] = useState(false)

  const totalDuration = toast.type === 'undo' ? 6000 : toast.type === 'error' ? 8000 : 3000

  useEffect(() => {
    const startTime = toast.expiresAt - totalDuration
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / totalDuration) * 100)
      setProgress(remaining)

      if (remaining <= 0) {
        clearInterval(interval)
      }
    }, 50)

    return () => clearInterval(interval)
  }, [toast.expiresAt, totalDuration])

  const handleDismiss = useCallback(() => {
    setIsExiting(true)
    setTimeout(onDismiss, 200)
  }, [onDismiss])

  const handleUndo = useCallback(() => {
    setIsExiting(true)
    setTimeout(onUndo, 200)
  }, [onUndo])

  const handleRetry = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => onRetry?.(), 200)
  }, [onRetry])

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <Check size={16} className="undo-toast-icon success" />
      case 'error':
        return <AlertTriangle size={16} className="undo-toast-icon error" />
      default:
        return <Undo2 size={16} className="undo-toast-icon undo" />
    }
  }

  return (
    <div className={clsx('undo-toast', `undo-toast--${toast.type}`, isExiting && 'exiting')}>
      <div className="undo-toast-content">
        {getIcon()}
        <span className="undo-toast-message">{toast.message}</span>
        <div className="undo-toast-actions">
          {toast.type === 'undo' && (
            <button className="undo-toast-undo" onClick={handleUndo}>
              <Undo2 size={14} />
              Undo
            </button>
          )}
          {toast.type === 'error' && toast.onRetry && (
            <button className="undo-toast-retry" onClick={handleRetry}>
              <RefreshCw size={14} />
              Retry
            </button>
          )}
          <button className="undo-toast-dismiss" onClick={handleDismiss} aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      </div>
      <div className="undo-toast-progress">
        <div
          className="undo-toast-progress-bar"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}

export function UndoToastContainer() {
  const { toasts, removeToast, executeUndo, executeRetry } = useUndoToastStore()

  if (typeof document === 'undefined') return null
  if (toasts.length === 0) return null

  return createPortal(
    <div className="undo-toast-container" role="region" aria-label="Notifications">
      {toasts.map(toast => (
        <SingleUndoToast
          key={toast.id}
          toast={toast}
          onUndo={() => executeUndo(toast.id)}
          onDismiss={() => removeToast(toast.id)}
          onRetry={toast.onRetry ? () => executeRetry(toast.id) : undefined}
        />
      ))}
    </div>,
    document.body
  )
}

// Legacy exports for backwards compatibility
export { SingleUndoToast as UndoToast }
export default UndoToastContainer
